import React, { ReactNode, useState } from "react";
import Button from "../Buttons/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  ColumnInterface,
  ExtraDataInterface,
} from "../../../interfaces/customTableInterface";
import formatToMoney from "../../../utils/formatToMoney";
import { getFullDateFormat } from "../../../utils/dateFormater";
import DownloadPDF from "./CustomActions/DownloadPDF";
import { ButtonIconProps } from "../Buttons/ButtonIcon";

export type ExtraAction<T> = Omit<
  ButtonIconProps,
  "onClick" | "size" | "children" | "icon" | "label"
> & {
  icon: NonNullable<ButtonIconProps["icon"]>;
  onClick: (row: T) => void;
  actionKey: string;
  shouldShow?: (row: T) => boolean;
};

export type CustomTableProps<T> = {
  data: T[];
  dataKey?: keyof T;
  columns: ColumnInterface<T>[];
  onEdit?: (data: T, index?: number) => void;
  onDelete?: (id: any) => void;
  tableId: string;
  extraData?: ExtraDataInterface<any>[];
  tfoot?: ReactNode | ReactNode[];
  downloadPDF?: boolean;
  customRender?: (key: keyof T, item: T) => void;
  extraActions?: ExtraAction<T>[];
  /**
   * When provided, header clicks call onSort(key) to request a new sorted
   * page from the server instead of re-sorting `data` in the browser.
   * `sortBy`/`sortOrder` then drive the header arrow instead of local state.
   * Boolean columns are not clickable in this mode.
   */
  sortBy?: keyof T;
  sortOrder?: "asc" | "desc";
  onSort?: (key: keyof T) => void;
};

const CustomTable = <T extends {}>({
  tableId,
  data,
  dataKey,
  columns,
  onEdit,
  onDelete,
  extraData,
  tfoot,
  downloadPDF,
  customRender,
  extraActions,
  sortBy,
  sortOrder,
  onSort,
}: CustomTableProps<T>) => {
  const isServerSort = !!onSort;
  const [sortedColumn, setSortedColumn] = useState<keyof T | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const getCellValue = (
    item: T,
    column: {
      key: keyof T;
      isMoney?: boolean;
      isDate?: boolean;
      isBoolean?: boolean;
    },
  ) => {
    if (extraData && extraData.some((x) => x.dataType === column.key)) {
      const srcData = extraData.find((x) => x.dataType === column.key);
      return srcData?.src.find((x) => x._id === item[column.key])?.name;
    } else if (column.isMoney) {
      return formatToMoney(item[column.key]);
    } else if (column.isDate) {
      return getFullDateFormat(item[column.key] as string);
    } else if (column.isBoolean) {
      return item[column.key] ? "Si" : "No";
    }
    return item[column.key];
  };

  const handleSort = (columnKey: keyof T) => {
    if (columnKey === sortedColumn) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortedColumn(columnKey);
      setSortDirection("asc");
    }
  };

  const sortedData = isServerSort
    ? data
    : [...data].sort((a, b) => {
        if (sortedColumn !== null) {
          const aValue = a[sortedColumn];
          const bValue = b[sortedColumn];
          if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
          if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        }
        return 0;
      });

  const handleClick = (data: T, index: number) => {
    if (tableId === "addresses-table") {
      onEdit!(data, index);
    } else {
      onEdit!(data);
    }
  };

  const showOptions = onEdit || onDelete || extraActions?.length;

  const renderOptions = (item: T, index: number) => (
    <td className="py-2 px-4 border-b">
      <div className="flex gap-2 justify-end">
        {onEdit && (
          <Button
            size="small"
            color="primary"
            onClick={() => handleClick(item, index)}
          >
            <FontAwesomeIcon icon={faEdit} />
          </Button>
        )}
        {downloadPDF && <DownloadPDF id={item["_id" as keyof T] as string} />}
        {onDelete && (
          <Button size="small" color="secondary" onClick={() => onDelete(item)}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        )}
        {extraActions &&
          extraActions.map((action) => {
            const { actionKey, shouldShow, ...buttonProps } = action;
            if (shouldShow && !shouldShow(item)) {
              return null;
            }
            return (
              <Button
                {...buttonProps}
                key={`${tableId}-${actionKey}-${index}`}
                size="small"
                color={action.color || "primary"}
                onClick={() => action.onClick(item)}
              >
                <FontAwesomeIcon size="lg" icon={action.icon} />
              </Button>
            );
          })}
      </div>
    </td>
  );

  return (
    <div className="border rounded-lg overflow-hidden mt-2 overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-gray-100">
          <tr>
            {columns.map((column) => {
              const isSortable =
                !isServerSort || (!column.isBoolean && column.sortable !== false);
              const isActive = isServerSort
                ? sortBy === column.key
                : sortedColumn === column.key;
              const direction = isServerSort ? sortOrder : sortDirection;
              return (
                <React.Fragment key={`header-${tableId}-${column.key as string}`}>
                  <th
                    className={`py-2 px-4 border-b ${isSortable ? "cursor-pointer" : ""}`}
                    onClick={() => {
                      if (!isSortable) return;
                      if (isServerSort) {
                        onSort!(column.key);
                      } else {
                        handleSort(column.key);
                      }
                    }}
                  >
                    {column.label}{" "}
                    {isActive && <span>{direction === "asc" ? "↑" : "↓"}</span>}
                  </th>
                </React.Fragment>
              );
            })}
            {showOptions && <th>Opciones</th>}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, index) => (
            <tr key={`columns-${tableId}-${dataKey ? item[dataKey] : index}`}>
              {columns.map((column) => (
                <React.Fragment key={`${tableId}-${column.key as string}`}>
                  <td key={column.key as string} className="py-2 px-4 border-b">
                    <>
                      {customRender?.(column.key, item) ??
                        getCellValue(item, column)}
                    </>
                  </td>
                </React.Fragment>
              ))}
              {showOptions && renderOptions(item, index)}
            </tr>
          ))}
        </tbody>
        {tfoot}
      </table>
    </div>
  );
};

export default CustomTable;
